import React, { useEffect } from "react";
import PropTypes from "prop-types";
import './interview-schedule-popups.css';
import { useDispatch, useSelector } from "react-redux";
import { fetchInterviewDetails, interviewCompleted } from "../../redux/Slices/common/interview/InterviewSlice";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import dayjs from "dayjs";
import { showSuccessOrError } from "../../services/commonService";
import SubmitSpinnerButton from "../ReusableComponents/SubmitSpinnerButton";
import translation from "../../utils/translation";

const validationSchema = yup.object().shape({
  type: yup.string().required("Report status is required"),
  comment: yup
    .string()
    .required("Comment is required")
    .min(10, "Comment must be at least 10 characters"),
});

const InterviewReportPopup = ({ interviewId, status, closeModal }) => {
  const dispatch = useDispatch();

  const {
    handleSubmit,
    control,
    formState: { errors },
    reset
  } = useForm({
    defaultValues: { type: '', comment: '' },
    resolver: yupResolver(validationSchema),
  });

  const { interviewDetail } = useSelector((state) => state.interview);
  
  useEffect(() => {
    dispatch(fetchInterviewDetails(interviewId));
  }, [interviewId]);

  useEffect(() => {
    if (interviewDetail?.comment) {
      reset({
        type: '',
        comment: interviewDetail.comment
      });
    }
  }, [interviewDetail, reset]);

  const onSubmit = (data) => {
    let formattedData = {
      ...data,
      interview_id: interviewId
    }
    dispatch(interviewCompleted(formattedData)).then(res => {
      showSuccessOrError(res);
      if(!res?.error){
        closeModal();
      }
    });
  }

  return (
    <div
      className="modal fade modalCustom newRegistrationJO addcontactInvoice show"
      id="addcontactInvoice"
      tabIndex={-1}
      aria-labelledby="exampleModalCenterTitle"
      style={{
        display: "block",
        paddingRight: "15px",
        background: "rgba(0,0,0,0.7)",
      }}
      aria-modal="true"
      role="dialog"
    >
      <div
        className="modal-dialog modal-dialog-centered mw740 w-100"
        role="document"
      >
        <div className="modal-content">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="modal-body formElement p-0">
              <div className="p24">
                <p className="report-title text-center blue mb24">
                  {translation('key_interview_report')}
                </p>
                <div className="pt-3">
                  <div className="d-flex justify-content-between">
                    <div className="userprofile_content d-flex grey9b mb20">
                       <figure className="mb-0" style={{ backgroundImage: `url(${interviewDetail?.image})` }}></figure>
                      <div className="contentWrap d-flex justify-content-center flex-column">
                        <p className="mb-1 text-truncate mw232 fz24 fw-700 regular black user-title text-uppercase text-nowrap mb8 mr10">{interviewDetail?.worker_fullname}</p>
                        <span className="grey fz16 fw-600 regular user-address">{interviewDetail?.age}, {interviewDetail?.worker_nationality_info}</span>
                      </div>
                    </div>
                    <div className="d-flex">
                      <div className="datetime d-flex justify-content-center flex-column mb20 mr-3">
                        <label className="form-label fz12 fw-700 grey9b mb-0 text-uppercase">{translation('key_date_and_time')}</label>
                        <p className="mb-0 gz16 fw-400 lh-normal">
                          {dayjs(`${interviewDetail?.interview_date} ${interviewDetail?.interview_time}`).format('ddd, D MMM, h:mm A')}
                        </p>
                      </div>
                      <div className="location d-flex justify-content-center flex-column mb20">
                        <label className="form-label fz12 fw-700 grey9b mb-0 text-uppercase">   { translation('key_location') }</label>
                        <p className='mb-0 gz16 fw-400 lh-normal'>
                          {interviewDetail?.office_location === 1 ? translation('key_video_interview') : translation('key_office_interview')} ({translation('key_employer')})
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="info-bar mb24 p12 blue w-100 br12">
                  {translation('key_please_select_one_report_status_for')} {interviewDetail?.worker_fullname}&lsquo;s &nbsp;
                  {translation('key_interview_with_employer')}&nbsp;
                  {interviewDetail?.employer_fullname}
                  </div>
                  <div className="mode-radio-buttons">
                    <div className="custom-radio compact mb16">
                      <Controller
                        control={control}
                        name="type"
                        render={({ field }) => (
                          <>
                            <input {...field} type="radio" name="reportStatus" id="interviewCompleted" value='completed' />
                            <label htmlFor="interviewCompleted" className="radio-label black">
                            {translation('key_interview_completed')}
                            </label>
                          </>
                        )}
                      />
                    </div>
                    <div className="custom-radio compact mb16">
                      <Controller
                        control={control}
                        name="type"
                        render={({ field }) => (
                          <>
                            <input {...field} type="radio" name="reportStatus" id="workerNoShow" value='worker_not_show' />
                            <label htmlFor="workerNoShow" className="radio-label black">
                              {translation('key_worker_no_show')}
                              </label>
                          </>
                        )}
                      />
                    </div>
                    <div className="custom-radio compact mb16">
                      <Controller
                        control={control}
                        name="type"
                        render={({ field }) => (
                          <>
                            <input {...field} type="radio" name="reportStatus" id="employerNoShow" value='employer_not_show' />
                            <label htmlFor="employerNoShow" className="radio-label black">
                              {translation('key_employer_no_show')}  
                            </label>
                          </>
                        )}
                      />
                    </div>
                    {errors.type && <small className="text-danger">{errors.type.message}</small>}
                  </div>
                  <div className="mt-3">
                    <label className="form-label regular">{translation('key_comment')}</label>
                    <Controller
                      name="comment"
                      control={control}
                      render={({ field }) => (
                        <textarea  {...field} className="form-control h-120" />
                      )}
                    />
                    {errors.comment && <small className="text-danger">{errors.comment.message}</small>}
                  </div>
                  <div className="w-100 mt-3">
                    {interviewDetail?.interview_report_marked_by_system ?
                      <p className="fz16 fw-400 grey2 mb-0">
                        {translation('key_interview_marked_by_system')} :{" "}
                        {interviewDetail?.interview_report_marked_at
                          ? dayjs(interviewDetail.interview_report_marked_at).format("ddd, D MMM, h:mm A")
                          : ""}
                      </p>
    
                      :
                      interviewDetail?.interview_report_marked_name && interviewDetail?.interview_report_marked_at && (
                        <p className="fz16 fw-400 grey2 mb-2"> {interviewDetail?.interview_report_marked_name} | {
                          dayjs(`${interviewDetail?.interview_report_marked_at}`).format('ddd, D MMM, h:mm A')
                        } </p>
                      )}

                      {interviewDetail?.cancelled_by_fullname && (
                        <p className="fz16 fw-400 grey2 mb-0">{translation('key_cancelled_by')} : {interviewDetail?.cancelled_by_fullname} </p>
                      )}
                  </div>
                </div>
              </div>
              <div className="modal-footer text-right">
                <button type="button" className="btn btn-outline-primary mr10" onClick={closeModal}>{translation('key_cancel')}</button>
                <SubmitSpinnerButton submitText='Submit' status={status} buttonClass="btn filled-btn mr12" />
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

InterviewReportPopup.propTypes = {
  closeModal: PropTypes.func,
  status: PropTypes.string,
  interviewId: PropTypes.object
};
export default InterviewReportPopup;